import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import popupBg from '../assets/images/popup-bg.png';
import { soundManager } from '../utils/soundUtils';

interface BluePopupProps {
  text?: string;
  children?: React.ReactNode;
  onClose?: () => void;
  isClosing?: boolean;
  fontSize?: string;
}

const PopupContainer = styled.div`
  width: 90vw;
  max-width: 85vw;
  aspect-ratio: 1.55 / 1;
  position: relative;
  margin: 0 auto;
  z-index: 10;
  background: none;
  overflow: visible;
`;

const PopupBg = styled.div.withConfig({
  shouldForwardProp: (prop) => !['open', 'isClosing'].includes(prop)
})<{ open: boolean; isClosing: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url(${popupBg});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  opacity: ${({ open, isClosing }) => {
    if (isClosing) return 0;
    return open ? 1 : 0;
  }};
  transform: translateY(${({ open, isClosing }) => {
    if (isClosing) return '20px';
    return open ? '0' : '30px';
  }});
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  z-index: 1;
`;

const PopupText = styled.div.withConfig({
  shouldForwardProp: (prop) => !['textVisible', 'fontSize'].includes(prop)
})<{ textVisible: boolean; fontSize?: string }>`
  width: 80%;
  position: absolute;
  top: 53%;
  left: 50%;
  transform: translate(-50%, -50%) translateY(${({ textVisible }) => textVisible ? '0' : '10px'});
  z-index: 2;
  font-family: 'MapoDPP';
  font-size: ${({ fontSize }) => fontSize || '1rem'};
  color: #fff;
  text-align: center;
  display: block;
  white-space: pre-line;

  opacity: ${({ textVisible }) => textVisible ? 1 : 0};
  transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const BluePopup: React.FC<BluePopupProps> = ({ text, children, onClose, isClosing = false, fontSize }) => {
  const [open, setOpen] = useState(false);
  const [textVisible, setTextVisible] = useState(false);

  useEffect(() => {
    if (isClosing) {
      // 텍스트 먼저 사라짐
      setTextVisible(false);
      // 텍스트 페이드아웃(0.2초) 후 배경 닫힘 시작
      const bgCloseTimer = setTimeout(() => {
        setOpen(false);
        // 배경 닫힘(0.3초) 후 onClose 호출
        const callbackTimer = setTimeout(() => {
          onClose?.();
        }, 500); // 배경 닫힘 시간
        return () => clearTimeout(callbackTimer);
      }, 200); // 텍스트 페이드아웃 시간
      return () => clearTimeout(bgCloseTimer);
    } else {
      // 팝업 시작 시 배경이 아래에서 위로 떠오르면서 나타남
      const openTimer = setTimeout(() => {
        setOpen(true);
        // 팝업이 나타날 때 사운드 재생
        soundManager.playPopupSound();
      }, 50);
      // 배경 애니메이션(0.3초) 후 텍스트 페이드인
      const textTimer = setTimeout(() => {
        setTextVisible(true);
      }, 350);
      return () => {
        clearTimeout(openTimer);
        clearTimeout(textTimer);
      };
    }
  }, [isClosing, onClose]);

  return (
    <PopupContainer>
      <PopupBg open={open} isClosing={isClosing} />
      <PopupText textVisible={textVisible} fontSize={fontSize}>
        {text
          ? text.split('\n').map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))
          : children}
      </PopupText>
      {children && (
        <div style={{ position: 'absolute', bottom: '7%', left: '50%', transform: 'translateX(-50%)', width: '80%', display: 'flex', justifyContent: 'center', gap: '3dvw', zIndex: 3 }}>
          {/* 버튼 등은 외부에서 렌더링 */}
        </div>
      )}
    </PopupContainer>
  );
};

export default BluePopup; 